import { gql, useApolloClient } from '@apollo/client'
import cx from 'classnames'
import { DashboardTitle } from 'components/Dashboard/DashboardTitleContext'
import PageComponent from 'components/Dashboard/PageComponent'
import SkuSelectInput from 'components/Dashboard/SkuSelectInput'
import TextInput from 'components/TextInput'
import { Form, Formik, useFormikContext } from 'formik'
import { Link, navigate } from 'gatsby'
import { useState } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import * as yup from 'yup'
import {
  CreateIdGenerationBatchMutation,
  CreateIdGenerationBatchMutationVariables,
} from './__generated__/CreateIdGenerationBatchMutation'
import { GenerateKitIdsMutation, GenerateKitIdsMutationVariables } from './__generated__/GenerateKitIdsMutation'

const generateKitIdsMutation = gql`
  mutation GenerateKitIdsMutation($quantity: Int!, $batchName: String!, $thirdPartyAssociateId: ID, $sku: String!) {
    generateKitIds(
      input: { quantity: $quantity, batchName: $batchName, thirdPartyAssociateId: $thirdPartyAssociateId, sku: $sku }
    ) {
      ids
    }
  }
`

const createIdGenerationBatchMutation = gql`
  mutation CreateIdGenerationBatchMutation(
    $quantity: Int!
    $batchName: String!
    $thirdPartyAssociateId: ID
    $sku: String!
  ) {
    createIdGenerationBatch(
      input: { quantity: $quantity, batchName: $batchName, thirdPartyAssociateId: $thirdPartyAssociateId, sku: $sku }
    ) {
      id
    }
  }
`

const validationSchema = yup.object().shape({
  quantity: yup.number().max(40000).required(),
  batchName: yup.string().required(),
  thirdPartyAssociateId: yup.string(),
})

const IdGenerationBatchForm = () => {
  const { isSubmitting } = useFormikContext()

  return (
    <Form>
      <TextInput
        name="quantity"
        label="Quantity"
        type="number"
        help="Quantities larger than 200 will create a long-running ID generation batch"
      />

      <TextInput name="batchName" label="Batch name" />

      <TextInput name="thirdPartyAssociateId" label="Third party ID" readOnly />

      <SkuSelectInput name="sku" type="physical_kit" />

      <div className="buttons">
        <button
          type="submit"
          className={cx('button is-primary', {
            'is-loading': isSubmitting,
          })}
        >
          Create IDs
        </button>
      </div>
    </Form>
  )
}

const GenerateKitIds: PageComponent = () => {
  const [thirdPartyId] = useQueryParam('thirdPartyId', StringParam)
  const [ids, setIds] = useState(null)

  const client = useApolloClient()

  const initialValues = {
    quantity: 10,
    batchName: '',
    thirdPartyAssociateId: thirdPartyId,
    sku: 'ncov',
  }

  return (
    <>
      <DashboardTitle title="Generate Kit IDs" />

      <section className="section is-small">
        <div className="container is-max-desktop">
          <div className="is-flex is-align-content-center is-justify-content-space-between mb-4">
            <div>
              <h1 className="title">Generate Kit IDs</h1>
            </div>

            <div>
              <Link to="/dashboard/id-generation-batches">ID Generation Batches</Link>
            </div>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              if (values.quantity > 200) {
                await client.mutate<CreateIdGenerationBatchMutation, CreateIdGenerationBatchMutationVariables>({
                  mutation: createIdGenerationBatchMutation,
                  variables: values,
                })

                navigate('/dashboard/id-generation-batches')
                return
              }

              const resp = await client.mutate<GenerateKitIdsMutation, GenerateKitIdsMutationVariables>({
                mutation: generateKitIdsMutation,
                variables: values,
              })

              if (resp.data.generateKitIds.ids) {
                setIds(resp.data.generateKitIds.ids)
              }
            }}
          >
            <IdGenerationBatchForm />
          </Formik>

          {ids && <pre>{ids.map((id) => `K${id.slice(0, 4)}-${id.slice(4)}\n`)}</pre>}
        </div>
      </section>
    </>
  )
}

GenerateKitIds.displayName = 'Generate Kit IDs'
GenerateKitIds.path = '/generate-kit-ids'

export default GenerateKitIds
